import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router';

import { useSession } from '@/features/auth/useSession';
import { PERMISSIONS } from '@/shared/permissions/permissions';
import type { Envelope } from '@/shared/types/api';
import type { PurchaseRequest } from '@/shared/types/domain';
import { openCart } from './api';

interface CartSelection {
  currency?: string;
  /** Changes only after the server accepts an add, never on a read/refetch. */
  pulse: number;
}

function selectionKey(tenantId?: string, userId?: string) {
  return ['cart-selection', tenantId, userId] as const;
}

function navigationKey(tenantId?: string, userId?: string, currency?: string) {
  return ['cart', 'navigation', tenantId, userId, currency ?? ''] as const;
}

/** A successful add selects its real currency basket; no optimistic counts. */
export function useCartAdditionFeedback() {
  const client = useQueryClient();
  const { session } = useSession();

  return (response: Envelope<PurchaseRequest>) => {
    const key = selectionKey(session?.tenant?.id, session?.user.id);
    const pulse = (client.getQueryData<CartSelection>(key)?.pulse ?? 0) + 1;
    client.setQueryData(['cart', response.data.currency], response);
    client.setQueryData(
      navigationKey(session?.tenant?.id, session?.user.id, response.data.currency),
      response,
    );
    client.setQueryData<CartSelection>(key, {
      currency: response.data.currency,
      pulse,
    });
    // Do not replay old success motion when a contextual menu later mounts.
    // An overlapping add owns its own timer; sign-out never recreates a cache.
    window.setTimeout(() => {
      client.setQueryData<CartSelection>(key, (current) =>
        current?.pulse === pulse ? { ...current, pulse: 0 } : current,
      );
    }, 200);
  };
}

/** Pure GET: mounting navigation never creates a cart or exposes another user. */
export function useCartNavigation() {
  const client = useQueryClient();
  const { session } = useSession();
  const location = useLocation();
  const selectionQueryKey = selectionKey(session?.tenant?.id, session?.user.id);
  /*
   * State, not a request: written by `useCartAdditionFeedback`, read here, never
   * fetched (`enabled: false`). It used to have no `queryFn` either, and in
   * development TanStack Query logs «No queryFn was passed» on EVERY render of a
   * query that has none, so the shell logged an error after every click. The
   * function below cannot run while the query is disabled; were it ever forced,
   * it would hand back what is already there.
   */
  const selection = useQuery<CartSelection>({
    queryKey: selectionQueryKey,
    queryFn: () => client.getQueryData<CartSelection>(selectionQueryKey) ?? { pulse: 0 },
    initialData: { pulse: 0 },
    enabled: false,
  });
  const currency =
    location.pathname === '/cart'
      ? (new URLSearchParams(location.search).get('currency') ?? undefined)
      : selection.data.currency;
  const cart = useQuery({
    queryKey: navigationKey(session?.tenant?.id, session?.user.id, currency),
    queryFn: () => openCart(undefined, currency),
    enabled: session?.permissions.includes(PERMISSIONS.requestCreate) ?? false,
    staleTime: 30_000,
  });
  const basket = cart.data?.data;
  const count =
    cart.error || cart.isPending
      ? null
      : (basket?.line_count ?? basket?.lines?.length ?? (basket === null ? 0 : null));

  return {
    count,
    href: currency ? `/cart?currency=${encodeURIComponent(currency)}` : '/cart',
    pulse: selection.data.pulse,
  };
}
